// CustomerSpaceBrandingContext.tsx
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    useEnsureBrandingFonts,
    usePublicFacilityBranding,
} from "../settings/brandings/useBranding";
import type { Branding } from "../settings/brandings/useBranding";
import { useStateContext } from "../../contexts/ContextProvider";
import { usePreviewDocument } from "../../components/dataDisplay/PreviewDocumentContext";

/** Mode déduit de la classe 'dark' sur <html> */
type Mode = "light" | "dark";
const getMode = (): Mode => {
    if (typeof document === "undefined") return "light";
    return document.documentElement.classList.contains("dark") ? "dark" : "light";
};

/** Contexte */
type Ctx = {
    branding?: Branding;
    isBranded: boolean;
    tokens: Record<string, string>; // seulement ce qu’on applique en inline
    mode: Mode;
};

const Ctx = createContext<Ctx>({
    branding: undefined,
    isBranded: false,
    tokens: {},
    mode: "light",
});

export const useCustomerSpaceBranding = () => useContext(Ctx);

/** util: "#rrggbb" -> "r g b" (laissée ici pour usage futur) */
function hexToRgbParts(hex?: string): string {
    if (!hex) return "0 0 0";
    const h = hex.replace("#", "");
    const norm = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    const bigint = parseInt(norm, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `${r} ${g} ${b}`;
}

/** helpers branding (laissés pour usage futur) */
function findColor(branding: Branding | undefined, key: string) {
    if (!branding) return undefined;
    const k = key.toLowerCase();
    return branding.brandingColors?.find(
        (c) => c.slug?.toLowerCase() === k || c.name?.toLowerCase() === k
    );
}
function toRgbIfSimple(c?: { color_type?: string; color1?: string }) {
    return c?.color_type === "simple" && c.color1 ? hexToRgbParts(c.color1) : undefined;
}

/** Valeurs par défaut (références aux valeurs d’index.css) – conservées pour plus tard */
const LIGHT_DEFAULTS = {
    title: "10 10 18",
    subtitle: "23 23 28",
    button: "7 5 34",
};
const DARK_DEFAULTS = {
    title: "255 255 255",
    subtitle: "120 121 152",
    button: "255 255 255",
};

/** Overrides LIGHT */
function computeLightOverrides(branding?: Branding): Record<string, string> {
    if (!branding) return {};
    const primary = findColor(branding, "primary") || findColor(branding, "principal");
    const primaryRgb = toRgbIfSimple(primary);
    const out: Record<string, string> = {};
    if (primaryRgb) {
        out["--cs-primary"] = primaryRgb;
        out["--cs-button"] = primaryRgb;
        out["--cs-icon-primary"] = primaryRgb; // En mode clair, icônes = couleur primaire
        out["--cs-text-primary"] = primaryRgb; // En mode clair, texte important = couleur primaire
    }
    // Couleurs d'état pour le mode clair
    out["--cs-error"] = "220 38 38"; // red-600
    out["--cs-error-bg"] = "254 242 242"; // red-50
    out["--cs-error-border"] = "254 202 202"; // red-200
    out["--cs-success"] = "22 163 74"; // green-600
    out["--cs-warning"] = "234 179 8"; // yellow-600
    return out;
}

/** Overrides DARK */
function computeDarkOverrides(branding?: Branding): Record<string, string> {
    if (!branding) return {};
    const primary = findColor(branding, "primary") || findColor(branding, "principal");
    const primaryRgb = toRgbIfSimple(primary);
    const out: Record<string, string> = {};
    if (primaryRgb) {
        out["--cs-primary"] = primaryRgb;
        out["--cs-button"] = primaryRgb;
    }
    // En mode sombre, icônes et textes importants = blanc/gris clair (plus lisible)
    out["--cs-icon-primary"] = "229 231 235"; // gray-200
    out["--cs-text-primary"] = "255 255 255"; // white - texte important en blanc pour visibilité
    // Couleurs d'état pour le mode sombre
    out["--cs-error"] = "248 113 113"; // red-400
    out["--cs-error-bg"] = "127 29 29"; // red-900/50
    out["--cs-error-border"] = "185 28 28"; // red-700
    out["--cs-success"] = "74 222 128"; // green-400
    out["--cs-warning"] = "250 204 21"; // yellow-400
    return out;
}

export const CustomerSpaceBrandingProvider: React.FC<{
    children: React.ReactNode;
    /** Optionnel : si non fourni, on prend user.facility_id */
    facilityId?: number | string;
    /** override live (partiel) */
    brandingOverride?: Partial<Branding>;
}> = ({ children, facilityId, brandingOverride }) => {
    const { user } = useStateContext();
    const resolvedFacilityId = facilityId ?? user?.facility_id;

    // Branding public actif du facility
    const { data: rawBranding } = usePublicFacilityBranding(resolvedFacilityId);
    const brandingFromApi: Branding | undefined = rawBranding ?? undefined;

    const branding: Branding | undefined = useMemo(() => {
        if (!brandingFromApi && !brandingOverride) return undefined;
        // fusion légère : les tableaux override priment
        return {
            ...(brandingFromApi ?? ({} as Branding)),
            ...(brandingOverride as any),
            brandingColors: brandingOverride?.brandingColors ?? brandingFromApi?.brandingColors,
            brandingFonts: brandingOverride?.brandingFonts ?? brandingFromApi?.brandingFonts,
        };
    }, [brandingFromApi, brandingOverride]);

    // Document cible : iframe (preview) si présent, sinon document global
    const previewDoc = usePreviewDocument();
    const targetDoc = previewDoc ?? (typeof document !== "undefined" ? document : null);

    // Injection des polices dans le bon document
    useEnsureBrandingFonts(targetDoc, branding);

    // Suivi du mode (classe 'dark' sur <html> du document cible)
    const [mode, setMode] = useState<Mode>(getMode());
    useEffect(() => {
        if (!targetDoc) return;
        const el = targetDoc.documentElement;
        const update = () =>
            setMode(el.classList.contains("dark") ? "dark" : "light");
        // init
        update();
        // observe
        const obs = new MutationObserver(update);
        obs.observe(el, { attributes: true, attributeFilter: ["class"] });
        return () => obs.disconnect();
    }, [targetDoc]);

    // Tokens par mode (pour l’instant vides)
    const lightTokens = useMemo(() => computeLightOverrides(branding), [branding]);
    const darkTokens = useMemo(() => computeDarkOverrides(branding), [branding]);

    // Mémo pour retirer proprement les overrides précédents
    const lastAppliedRef = useRef<string[]>([]);

    useEffect(() => {
        if (!targetDoc) return;
        const root = targetDoc.documentElement;

        // Pas de branding -> on enlève tout = on laisse index.css (light/dark) faire le job
        if (!branding) {
            lastAppliedRef.current.forEach((k) => root.style.removeProperty(k));
            lastAppliedRef.current = [];
            root.removeAttribute("data-cs-branded");
            return;
        }

        // Sélection des overrides selon le mode
        const tokens = mode === "dark" ? darkTokens : lightTokens;

        // Si aucun override calculé pour ce mode => on nettoie tout
        if (!tokens || Object.keys(tokens).length === 0) {
            lastAppliedRef.current.forEach((k) => root.style.removeProperty(k));
            lastAppliedRef.current = [];
            root.removeAttribute("data-cs-branded");
            return;
        }

        // Applique uniquement les clés prévues (on ne touche pas aux autres CSS vars)
        lastAppliedRef.current.forEach((k) => root.style.removeProperty(k));
        Object.entries(tokens).forEach(([k, v]) => root.style.setProperty(k, v));
        lastAppliedRef.current = Object.keys(tokens);
        root.setAttribute("data-cs-branded", mode); // indicatif

        // Cleanup si branding/mode change
        return () => {
            lastAppliedRef.current.forEach((k) => root.style.removeProperty(k));
            lastAppliedRef.current = [];
            root.removeAttribute("data-cs-branded");
        };
    }, [targetDoc, branding, mode, lightTokens, darkTokens]);

    // (Optionnel) Injection manuelle complémentaire des polices dans le bon document
    // Conservée pour éviter toute régression si certaines familles ne passent pas par useEnsureBrandingFonts.
    useEffect(() => {
        if (!targetDoc) return;
        const fonts = (branding as any)?.brandingFonts as any[] | undefined;
        if (!fonts || fonts.length === 0) return;

        const addedNodes: HTMLElement[] = [];
        const seenImports = new Set<string>();

        for (const f of fonts) {
            const ff = (f?.fontFamily ?? f?.font_family) as any | undefined;
            const cssImport: string | undefined = ff?.import; // ex: "@import url('https://...');" ou bloc @font-face
            if (!cssImport || seenImports.has(cssImport)) continue;
            seenImports.add(cssImport);

            // Si c'est un @import url("..."), on préfère un <link rel="stylesheet">
            const m = cssImport.match(/@import\s+url\((['"]?)(.*?)\1\)\s*;?/i);
            if (m?.[2]) {
                const href = m[2];
                const link = targetDoc.createElement("link");
                link.rel = "stylesheet";
                link.href = href;
                link.setAttribute("data-cs-font", "1");
                targetDoc.head.appendChild(link);
                addedNodes.push(link);
            } else {
                // Sinon on injecte tel quel (utile pour @font-face)
                const styleEl = targetDoc.createElement("style");
                styleEl.setAttribute("data-cs-font", "1");
                styleEl.append(targetDoc.createTextNode(cssImport));
                targetDoc.head.appendChild(styleEl);
                addedNodes.push(styleEl);
            }
        }

        // cleanup au changement de branding / document cible
        return () => {
            addedNodes.forEach((el) => el.parentNode?.removeChild(el));
        };
    }, [targetDoc, branding]);

    // Expose les tokens effectivement appliqués (utile pour debug/aperçu UI)
    const activeTokens = useMemo(
        () => (mode === "dark" ? darkTokens : lightTokens),
        [mode, lightTokens, darkTokens]
    );

    const value: Ctx = useMemo(
        () => ({
            branding,
            isBranded: !!branding,
            tokens: activeTokens,
            mode,
        }),
        [branding, activeTokens, mode]
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
